import React, { useEffect, useState } from 'react';
import Drawer, { DrawerField, DrawerSection } from '../../components/overlays/Drawer';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import { SkeletonBlock } from '../../components/ui/Skeleton';
import { agentServicesGet } from '../../actions/agentAction';
import { useDispatch } from 'react-redux';
import { statusLabel } from '../../design/tokens';

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Partner detail drawer. Replaces the previous full-page /agent-onboarding/:id
 * route for review work — the list stays in place behind it.
 */
export default function PartnerDrawer({ partner, open, onClose, onStatusChange }) {
  const dispatch = useDispatch();
  const [services, setServices] = useState(null);

  useEffect(() => {
    if (!open || !partner) {
      setServices(null);
      return;
    }
    let cancelled = false;
    agentServicesGet(dispatch, partner.id)
      .then((rows) => {
        if (!cancelled) setServices(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setServices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, partner, dispatch]);

  if (!partner) return null;

  const phone = partner.phone_number
    ? `${partner.phone_number.country_code || ''} ${partner.phone_number.number || ''}`.trim()
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={partner.name || partner.email || 'Partner'}
      status={partner.status}
      footer={
        <>
          <Button
            variant="primary"
            onClick={() => onStatusChange(partner, 'live')}
            disabled={partner.status === 'live'}
          >
            Approve
          </Button>
          <Button
            variant="secondary"
            onClick={() => onStatusChange(partner, 'on_hold')}
            disabled={partner.status === 'on_hold'}
          >
            Put on hold
          </Button>
          <Button variant="danger-text" onClick={() => onStatusChange(partner, 'blacklisted')}>
            Blacklist
          </Button>
        </>
      }
    >
      <DrawerSection title="Identity">
        <DrawerField label="Name">{partner.name}</DrawerField>
        <DrawerField label="Gender">{partner.properties?.gender}</DrawerField>
        <DrawerField label="Experience">
          {partner.properties?.experience ? `${partner.properties.experience} years` : null}
        </DrawerField>
        <DrawerField label="Joined">{formatDate(partner.created_at)}</DrawerField>
      </DrawerSection>

      <DrawerSection title="Contact">
        <DrawerField label="Email">{partner.email}</DrawerField>
        <DrawerField label="Phone" mono>
          {phone}
        </DrawerField>
      </DrawerSection>

      <DrawerSection title="Bookings">
        <DrawerField label="Total sessions" mono>
          {partner.bookings ?? 0}
        </DrawerField>
        <DrawerField label="Booking status">
          {partner.booking_status?.status ? (
            <StatusPill status={partner.booking_status.status} />
          ) : null}
        </DrawerField>
        <DrawerField label="Bank verification">
          {partner.bank_status ? <StatusPill status={partner.bank_status} /> : 'Not submitted'}
        </DrawerField>
      </DrawerSection>

      <DrawerSection title="Profile">
        {/* The drawer is review-only per the handoff; editing bio, expertise and
            services still lives on the full profile screen, so keep a way in. */}
        <Button
          variant="secondary"
          icon="bx-link-external"
          onClick={() => window.open(`/agent-onboarding/${partner.id}`, '_self')}
        >
          Open full profile
        </Button>
      </DrawerSection>

      <DrawerSection title={`Services (${services?.length ?? 0})`}>
        {services === null && (
          <div style={{ padding: '8px 0' }}>
            <SkeletonBlock width="80%" height={12} />
          </div>
        )}
        {services?.length === 0 && (
          <div className="sv-drawer-field__label" style={{ padding: '8px 0' }}>
            No services yet.
          </div>
        )}
        {services?.map((service) => (
          <DrawerField key={service.id} label={service.title}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="sv-mono">
                {service.currency === 'INR' ? '₹' : '$'}
                {service.price} · {service.duration}m
              </span>
              <StatusPill status={service.status}>{statusLabel(service.status)}</StatusPill>
            </span>
          </DrawerField>
        ))}
      </DrawerSection>
    </Drawer>
  );
}
